<script>
  import Async from '../components/Async.svelte';
  import Button from '../components/Button.svelte';
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import DataTable from '../components/DataTable.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getDatasets, getDashboard, getDashboardPartners, rebuildDashboard } from '../lib/api.js';
  import { HELP } from '../lib/help.js';
  import { mayVisit } from '../lib/session.svelte.js';
  import { periodRange } from '../lib/datasets.js';

  // The machinery behind the datasets: first how each dataset is built, then
  // one card per part, in the order the data flows: the terms, where data
  // comes from, what is stored, what is counted. Each card's figures are per
  // dataset, so what belongs to what stays visible; a dataset's own detail is
  // on its page (Under the hood).
  async function load() {
    const [{ datasets }, dashboard] = await Promise.all([getDatasets(), getDashboard()]);
    return { datasets, dashboard };
  }
  let state = $state(load());
  let rebuilding = $state(false);
  let rebuildError = $state(null);

  async function rebuildStatistics() {
    rebuilding = true;
    rebuildError = null;
    try {
      await rebuildDashboard();
      state = load();
    } catch (error) {
      rebuildError = error.message;
    } finally {
      rebuilding = false;
    }
  }

  const showSources = $derived(mayVisit('sources'));
  const showRecords = $derived(mayVisit('records'));
  const showCubes = $derived(mayVisit('catalog'));
  const partners = $derived(showCubes ? getDashboardPartners() : null);

  const allTerms = (datasets) => [...new Map(datasets.flatMap((d) => d.terms.flatMap((s) => s.terms))
    .map((t) => [t.id, t])).values()];
  const prefixes = (datasets) => {
    const counts = new Map();
    for (const term of allTerms(datasets)) counts.set(term.prefix, (counts.get(term.prefix) ?? 0) + 1);
    return [...counts].map(([prefix, count]) => `${prefix}: ${count}`).join(' · ');
  };
  // The sources in a line; the list itself is on Sources.
  const labelOf = (s) => s.state?.label ?? s.connector.sourceLabel ?? s.sourceId;
  const names = (list) => list.map(labelOf).join(', ');
  function sourcesLine(sources, datasets) {
    const filled = datasets.filter((d) => d.sources.length).length;
    const lines = [`${sources.length} ${sources.length === 1 ? 'source fills' : 'sources fill'} ${filled} ${filled === 1 ? 'dataset' : 'datasets'}.`];
    const pending = sources.filter((s) => s.state?.mode !== 'snapshot' && s.state?.pending);
    const awaiting = sources.filter((s) => s.state?.mode === 'awaiting-upload');
    if (pending.length) lines.push(`New rows waiting: ${names(pending)}.`);
    if (awaiting.length) lines.push(`Waiting for a first file: ${names(awaiting)}.`);
    return lines.join(' ');
  }
  const hood = (id) => `#/data/dataset?id=${encodeURIComponent(id)}&tab=hood`;
</script>

<HelpDrawer {...HELP.advanced} />

<h1 class="wf2-title wf2-title-level-1">Advanced</h1>
<p class="wf2-subtitle">How the datasets are made: the terms they use, where their data comes from, what is
  stored and what is counted for sharing.</p>

<Async promise={state}>
  {#snippet children({ datasets, dashboard })}
    {#if dashboard.freshness.state !== 'current'}
      <div class="state error">
        The figures on this page are {dashboard.freshness.state}.
        {#if mayVisit('sources')}
          <Button onclick={rebuildStatistics} disabled={rebuilding}>{rebuilding ? 'Rebuilding…' : 'Rebuild statistics'}</Button>
        {:else}
          Ask a data steward to rebuild them.
        {/if}
        {#if rebuildError} {rebuildError}{/if}
      </div>
    {/if}

    <div class="cards">
      <div class="wide"><Section title="Datasets">
        <Panel>
          <p class="wf2-text">What each dataset is built from: the record classes it stores, the sources that fill
            it, the terms it uses and the cubes that count it.</p>
          {#if datasets.length}
            <div class="wf2-tablewrap"><table class="wf2-table">
              <thead><tr><th>Dataset</th><th>Record classes</th><th>Sources</th><th>Terms</th><th>Cubes</th></tr></thead>
              <tbody>
                {#each datasets as d}
                  <tr>
                    <td><a href={hood(d.id)}>{d.label}</a></td>
                    <td>{d.recordClasses.length}</td>
                    <td>{d.sources.length}</td>
                    <td>{d.terms.reduce((n, s) => n + s.terms.length, 0)}</td>
                    <td>{d.sharing.cubes.length}</td>
                  </tr>
                {/each}
              </tbody>
            </table></div>
          {:else}
            <p class="wf2-text">No dataset yet.</p>
          {/if}
          {#if mayVisit('dataset/new')}
            <div class="wf2-row" style="gap:12px">
              <Button variant="secondary" to="dataset/new">New dataset from a file</Button>
              <Button variant="secondary" to="dataset/new?add=1">Add a source to a dataset</Button>
            </div>
          {/if}
        </Panel>
      </Section></div>

      <Section title="Schemas">
        <Panel>
          <p class="wf2-text">Define the terms the datasets use. Written offline as Turtle, uploaded here.</p>
          <DataTable rows={[
            ['Terms', `${allTerms(datasets).length} (${prefixes(datasets) || 'none'})`],
            ['Record classes', `${datasets.reduce((n, d) => n + d.recordClasses.length, 0)}`],
          ]} />
          <p class="wf2-text">Anyone can read it, at <a href="/schema/" target="_blank" rel="noopener">{location.origin}/schema/</a>, which is where its terms' IRIs lead.</p>
          {#if mayVisit('schemas')}<Button variant="secondary" to="schemas">Schemas</Button>{/if}
        </Panel>
      </Section>

      {#if showSources}
        <Section title="Sources">
          <Panel>
            <p class="wf2-text">Manage where the data in each dataset comes from.</p>
            <p class="wf2-text">{dashboard.sources.length ? sourcesLine(dashboard.sources, datasets) : 'No source yet.'}</p>
            <div class="wf2-row" style="gap:12px">
              <Button variant="primary" to="sources">Sources</Button>
              {#if mayVisit('dataset/new')}<Button variant="secondary" to="dataset/new?add=1">New source</Button>{/if}
            </div>
          </Panel>
        </Section>
      {/if}

      {#if showRecords}
        <Section title="Records">
          <Panel>
            <p class="wf2-text">The ingested data of each dataset, kept privately in your Pod.</p>
            <DataTable columns={['Dataset', 'Records', 'Periods']} rows={datasets.map((d) =>
              [d.label, d.records === null ? 'statistics unavailable' : `${d.records}`, periodRange(d.periods)])} />
            <Button variant="primary" to="records">Browse records</Button>
          </Panel>
        </Section>
      {/if}

      {#if showCubes}
        <Section title="Cubes">
          <Panel>
            <p class="wf2-text">Slices of a dataset that make responsible sharing possible: only counts leave, never records.</p>
            <DataTable columns={['Dataset', 'Cubes', 'Shared with']} rows={datasets.map((d) =>
              [d.label, `${d.sharing.cubes.length}`, d.sharing.partners.map((p) => p.label).join(', ') || '—'])} />
            <Async promise={partners} loading="Checking what partners share…">
              {#snippet children(remote)}
                <p class="wf2-caption">From partners: {remote.partners.map((p) =>
                  `${p.label} ${p.readableCount ? `${p.readableCount} of ${p.inboundCount} readable` : p.inboundCount ? 'pending' : 'nothing yet'}`).join(' · ') || 'no partners'}.</p>
              {/snippet}
            </Async>
            <div class="wf2-row" style="gap:12px">
              <Button variant="primary" to="catalog">Browse cubes</Button>
              {#if mayVisit('catalog/new')}<Button variant="secondary" to="catalog/new">New cube</Button>{/if}
            </div>
          </Panel>
        </Section>
      {/if}
    </div>
  {/snippet}
</Async>

<style>
  .cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 var(--space-4); }
  @media (max-width: 860px) { .cards { grid-template-columns: 1fr; } }
  .wide { grid-column: 1 / -1; }
</style>
